import { useState, useMemo } from 'react';
// NOTE: recharts must be installed — run: npm install recharts
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore -- recharts may not be installed yet
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import type { AggregatedDataPoint } from '../../types/reportTypes';
import '../../css/ReportVisualization.css';

const CHART_COLORS = [
    '#013248', '#F98733', '#3b82f6', '#22c55e', '#eab308',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

const COMPARISON_COLORS = [
    '#94a3b8', '#fbbf24', '#93c5fd', '#86efac', '#fde047',
    '#c4b5fd', '#f9a8d4', '#5eead4', '#fdba74', '#a5b4fc',
];

type SortDirection = 'asc' | 'desc';
type SortField = 'groupLabel' | 'value' | 'count';

interface ReportVisualizationProps {
    data: AggregatedDataPoint[];
    visualizationType: 'table' | 'bar' | 'pie' | 'line';
    comparisonData?: AggregatedDataPoint[];
    isLoading: boolean;
    metricLabel?: string;
    groupByLabel?: string;
}

/**
 * Shared visualization component for report data.
 * Renders table, bar chart, pie chart, or line chart based on visualizationType.
 * Supports comparison mode for bar and line charts.
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7
 */
export const ReportVisualization = ({
    data,
    visualizationType,
    comparisonData,
    isLoading,
    metricLabel = 'Value',
    groupByLabel = 'Group',
}: ReportVisualizationProps) => {
    if (isLoading) {
        return (
            <div className="report-viz-container">
                <div className="report-viz-loading" role="status" aria-live="polite">
                    <div className="report-viz-spinner" aria-hidden="true" />
                    <span className="report-viz-loading-text">Loading report data...</span>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="report-viz-container">
                <div className="report-viz-empty" role="status">
                    <span className="report-viz-empty-icon" aria-hidden="true">📊</span>
                    <span className="report-viz-empty-text">No data available for the selected configuration.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="report-viz-container">
            {visualizationType === 'table' && (
                <TableView data={data} metricLabel={metricLabel} groupByLabel={groupByLabel} />
            )}
            {visualizationType === 'bar' && (
                <BarChartView
                    data={data}
                    comparisonData={comparisonData}
                    metricLabel={metricLabel}
                    groupByLabel={groupByLabel}
                />
            )}
            {visualizationType === 'pie' && (
                <PieChartView data={data} metricLabel={metricLabel} />
            )}
            {visualizationType === 'line' && (
                <LineChartView
                    data={data}
                    comparisonData={comparisonData}
                    metricLabel={metricLabel}
                    groupByLabel={groupByLabel}
                />
            )}
        </div>
    );
};


function formatMetricValue(value: number): string {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Text list of the same points drawn in the chart, for screen readers. */
function ChartDataList({
    data,
    metricLabel,
}: {
    data: AggregatedDataPoint[];
    metricLabel: string;
}) {
    return (
        <ul className="report-viz-visually-hidden">
            {data.map((row) => (
                <li key={row.groupKey}>
                    {row.groupLabel}: {formatMetricValue(row.value)} {metricLabel}
                </li>
            ))}
        </ul>
    );
}

// ─── Table View ─────────────────────────────────────────────────────────────

function TableView({
    data,
    metricLabel,
    groupByLabel,
}: {
    data: AggregatedDataPoint[];
    metricLabel: string;
    groupByLabel: string;
}) {
    const [sortField, setSortField] = useState<SortField>('groupLabel');
    const [sortDir, setSortDir] = useState<SortDirection>('asc');

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const sortedData = useMemo(() => {
        const sorted = [...data];
        sorted.sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            const numA = Number(aVal);
            const numB = Number(bVal);
            return sortDir === 'asc' ? numA - numB : numB - numA;
        });
        return sorted;
    }, [data, sortField, sortDir]);

    const renderSortIndicator = (field: SortField) => {
        const isActive = sortField === field;
        const arrow = isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
        return <span className={`sort-indicator ${isActive ? 'active' : ''}`} aria-hidden="true">{arrow}</span>;
    };

    const ariaSort = (field: SortField): 'ascending' | 'descending' | 'none' => {
        if (sortField !== field) return 'none';
        return sortDir === 'asc' ? 'ascending' : 'descending';
    };

    return (
        <div className="report-viz-table-wrapper">
            <table className="report-viz-table">
                <caption className="report-viz-visually-hidden">
                    {metricLabel} by {groupByLabel}
                </caption>
                <thead>
                    <tr>
                        <th aria-sort={ariaSort('groupLabel')}>
                            <button type="button" className="report-viz-sort-btn" onClick={() => handleSort('groupLabel')}>
                                {groupByLabel} {renderSortIndicator('groupLabel')}
                            </button>
                        </th>
                        <th aria-sort={ariaSort('value')}>
                            <button type="button" className="report-viz-sort-btn" onClick={() => handleSort('value')}>
                                {metricLabel} {renderSortIndicator('value')}
                            </button>
                        </th>
                        <th aria-sort={ariaSort('count')}>
                            <button type="button" className="report-viz-sort-btn" onClick={() => handleSort('count')}>
                                Count {renderSortIndicator('count')}
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((row) => (
                        <tr key={row.groupKey}>
                            <td>{row.groupLabel}</td>
                            <td>{typeof row.value === 'number' ? formatMetricValue(row.value) : row.value}</td>
                            <td>{row.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Bar Chart View ─────────────────────────────────────────────────────────

function BarChartView({
    data,
    comparisonData,
    metricLabel,
    groupByLabel,
}: {
    data: AggregatedDataPoint[];
    comparisonData?: AggregatedDataPoint[];
    metricLabel: string;
    groupByLabel: string;
}) {
    const hasComparison = comparisonData && comparisonData.length > 0;

    // Merge primary and comparison data for side-by-side bars
    const chartData = useMemo(() => {
        if (!hasComparison) {
            return data.map((d) => ({ name: d.groupLabel, primary: d.value }));
        }
        const compMap = new Map(comparisonData!.map((d) => [d.groupKey, d.value]));
        return data.map((d) => ({
            name: d.groupLabel,
            primary: d.value,
            comparison: compMap.get(d.groupKey) ?? 0,
        }));
    }, [data, comparisonData, hasComparison]);

    return (
        <div className="report-viz-chart" role="group" aria-label={`${metricLabel} by ${groupByLabel}`}>
            <ChartDataList data={data} metricLabel={metricLabel} />
            <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} label={{ value: groupByLabel, position: 'insideBottom', offset: -5, fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} label={{ value: metricLabel, angle: -90, position: 'insideLeft', fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="primary" name={metricLabel} fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                    {hasComparison && (
                        <Bar dataKey="comparison" name={`${metricLabel} (Comparison)`} fill={COMPARISON_COLORS[0]} radius={[4, 4, 0, 0]} />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ─── Pie Chart View ─────────────────────────────────────────────────────────

function PieChartView({
    data,
    metricLabel,
}: {
    data: AggregatedDataPoint[];
    metricLabel: string;
}) {
    const chartData = data.map((d) => ({ name: d.groupLabel, value: d.value }));

    return (
        <div className="report-viz-chart" role="group" aria-label={metricLabel}>
            <ChartDataList data={data} metricLabel={metricLabel} />
            <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                    <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                        {chartData.map((_entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => [String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ','), metricLabel]} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

// ─── Line Chart View ────────────────────────────────────────────────────────

function LineChartView({
    data,
    comparisonData,
    metricLabel,
    groupByLabel,
}: {
    data: AggregatedDataPoint[];
    comparisonData?: AggregatedDataPoint[];
    metricLabel: string;
    groupByLabel: string;
}) {
    const hasComparison = comparisonData && comparisonData.length > 0;

    const chartData = useMemo(() => {
        if (!hasComparison) {
            return data.map((d) => ({ name: d.groupLabel, primary: d.value }));
        }
        const compMap = new Map(comparisonData!.map((d) => [d.groupKey, d.value]));
        return data.map((d) => ({
            name: d.groupLabel,
            primary: d.value,
            comparison: compMap.get(d.groupKey) ?? 0,
        }));
    }, [data, comparisonData, hasComparison]);

    return (
        <div className="report-viz-chart" role="group" aria-label={`${metricLabel} by ${groupByLabel}`}>
            <ChartDataList data={data} metricLabel={metricLabel} />
            <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} label={{ value: groupByLabel, position: 'insideBottom', offset: -5, fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} label={{ value: metricLabel, angle: -90, position: 'insideLeft', fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="primary" name={metricLabel} stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />
                    {hasComparison && (
                        <Line type="monotone" dataKey="comparison" name={`${metricLabel} (Comparison)`} stroke={COMPARISON_COLORS[0]} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
